import { useState, useEffect, useRef } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { QuoteRequestDialog } from "@/components/common/QuoteRequestDialog";
import { AirportAutocomplete } from "@/components/common/AirportAutocomplete";
import { DestinationAutocomplete } from "@/components/common/DestinationAutocomplete";
import { CruisePortAutocomplete } from "@/components/common/CruisePortAutocomplete";

// ── Animated tab icons ─────────────────────────────────────────────────────────
const FlightTabIcon = ({ active }) => {
  const c = active ? "#21b3ed" : "#9ca3af";
  const cc = active ? "#090e4c" : "#d1d5db";
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline-block">
      {/* contrail dots */}
      <circle cx="2" cy="14" r="1.2" fill={c} style={{ opacity: active ? 1 : 0, animation: active ? "tab-ring-pulse 1.4s ease-out infinite 0.2s" : "none" }} />
      <circle cx="5.5" cy="15.5" r="1" fill={c} style={{ opacity: active ? 0.6 : 0, animation: active ? "tab-ring-pulse 1.4s ease-out infinite 0.5s" : "none" }} />
      {/* airplane body */}
      <g className={active ? "tab-plane-float" : ""}>
        <path d="M21 8.5l-8.5 5.5H7l-2.5-2 2.5-1h3L13 8.5H9L7 7l2-0.5h3.5L21 8.5z" fill={cc} />
        <path d="M10 11.5l1.5 3H13l1-3" fill={c} />
        <path d="M21 8.5c0 0-1 1-3 1.5s-3.5 0-3.5 0" stroke={c} strokeWidth="0.5" strokeLinecap="round" />
      </g>
    </svg>
  );
};

const ToursTabIcon = ({ active }) => {
  const c = active ? "#21b3ed" : "#9ca3af";
  const cc = active ? "#090e4c" : "#d1d5db";
  return (
    <svg width="20" height="22" viewBox="0 0 20 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline-block">
      <g className={active ? "tab-pin-bounce" : ""}>
        {/* pin body */}
        <path d="M10 2C6.686 2 4 4.686 4 8c0 4.5 6 12 6 12s6-7.5 6-12c0-3.314-2.686-6-6-6z" fill={cc} />
        {/* pin hole */}
        <circle cx="10" cy="8" r="2.5" fill="white" />
        {/* inner dot */}
        <circle cx="10" cy="8" r="1.2" fill={c} />
      </g>
      {/* pulse ring (active only) */}
      {active && (
        <circle cx="10" cy="8" r="4" stroke={c} strokeWidth="1" fill="none"
          style={{ animation: "tab-ring-pulse 1.6s ease-out infinite" }} />
      )}
    </svg>
  );
};

const CruisesTabIcon = ({ active }) => {
  const c = active ? "#21b3ed" : "#9ca3af";
  const cc = active ? "#090e4c" : "#d1d5db";
  return (
    <svg width="24" height="22" viewBox="0 0 26 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline-block overflow-visible">
      {/* ship hull */}
      <g className={active ? "tab-ship-rock" : ""}>
        {/* hull */}
        <path d="M3 16l2-5h14l2 5H3z" fill={cc} />
        {/* deck */}
        <rect x="6" y="9" width="12" height="4" rx="1" fill={c} />
        {/* smokestack */}
        <rect x="10" y="5" width="3" height="5" rx="1" fill={cc} />
        {/* smoke puff (active only) */}
        {active && <>
          <circle cx="11.5" cy="4" r="1.2" fill="#e5e7eb" style={{ animation: "tab-pin-bounce 2s ease-in-out infinite" }} />
          <circle cx="13" cy="2.5" r="0.9" fill="#e5e7eb" style={{ animation: "tab-pin-bounce 2s ease-in-out infinite 0.3s" }} />
        </>}
        {/* porthole */}
        <circle cx="8" cy="13" r="1" fill="white" />
        <circle cx="12" cy="13" r="1" fill="white" />
        <circle cx="16" cy="13" r="1" fill="white" />
        {/* flag */}
        <line x1="12" y1="5" x2="12" y2="2" stroke={c} strokeWidth="1" strokeLinecap="round" />
        <path d="M12 2l3 1-3 1z" fill={c} />
      </g>
      {/* wave */}
      <path d={`M1 19.5 Q4.5 17.5 8 19.5 Q11.5 21.5 15 19.5 Q18.5 17.5 22 19.5 Q25 21.5 25 21.5`}
        stroke={c} strokeWidth="1.5" fill="none" strokeLinecap="round"
        style={{ opacity: active ? 0.7 : 0.3 }} />
    </svg>
  );
};

// ── Hero background ────────────────────────────────────────────────────────────
const heroImages = [
  "/hero/hero-1.png","/hero/hero-2.png","/hero/hero-3.png",
  "/hero/hero-4.png","/hero/hero-5.png","/hero/hero-6.png",
];

const HeroBackground = () => {
  const [idx, setIdx] = useState(0);
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const h = e => setReduced(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  useEffect(() => {
    if (reduced) return;
    const t = setInterval(() => setIdx(p => (p + 1) % heroImages.length), 5500);
    return () => clearInterval(t);
  }, [reduced]);
  return (
    <div className="absolute inset-0 overflow-hidden">
      {heroImages.map((src, i) => (
        <div key={src} className="absolute inset-0 transition-opacity duration-[1200ms]" style={{ opacity: i === idx ? 1 : 0 }}>
          <img src={src} alt="" className="w-full h-full object-cover" loading={i < 2 ? "eager" : "lazy"} />
        </div>
      ))}
      <div className="absolute inset-0" style={{ background:"linear-gradient(to bottom,rgba(9,14,76,.55) 0%,rgba(9,14,76,.45) 60%,rgba(9,14,76,.65) 100%)" }} />
    </div>
  );
};

// ── Date helpers ───────────────────────────────────────────────────────────────
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WDAYS  = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
function todayStr() { return new Date().toISOString().split("T")[0]; }
function daysInMonth(y,m) { return new Date(y,m+1,0).getDate(); }
function firstDayOf(y,m)  { return new Date(y,m,1).getDay(); }
function datStr(y,m,d)    { return `${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`; }
function fmtDate(ds) {
  if (!ds) return "";
  const d = new Date(ds+"T12:00:00");
  return `${WDAYS[d.getDay()]}, ${MONTHS[d.getMonth()].slice(0,3)} ${d.getDate()}`;
}

// ── Dual-month calendar ────────────────────────────────────────────────────────
const FLEX_OPTIONS = [
  { value: 0, label: "Exact dates" },
  { value: 1, label: "±1 day" },
  { value: 2, label: "±2 days" },
  { value: 3, label: "±3 days" },
];

function DateRangePicker({ start, end, onChange, onClose, singleDate, autoClose = false, flexibility = 0, onFlexChange }) {
  const today = todayStr();
  const now   = new Date();
  const [yr, setYr] = useState(now.getFullYear());
  const [mo, setMo] = useState(now.getMonth());
  const [hover, setHover] = useState(null);
  const [phase, setPhase] = useState(start ? "end" : "start");
  const [flex, setFlex]   = useState(flexibility);

  const r_mo = mo === 11 ? 0 : mo + 1;
  const r_yr = mo === 11 ? yr + 1 : yr;

  const handleFlexChange = (v) => {
    setFlex(v);
    if (onFlexChange) onFlexChange(v);
  };

  const pick = (ds) => {
    if (ds < today) return;
    if (singleDate || phase === "start" || !start) {
      onChange(ds, null, flex);
      if (autoClose) { onClose(); return; }   // multi-city legs: close immediately
      if (!singleDate) setPhase("end");       // roundtrip: advance to return phase
    } else {
      if (ds > start) {
        onChange(start, ds, flex);
        setPhase("start");
      } else {
        // User clicked a date ≤ current departure — start over with this as new departure
        onChange(ds, null, flex);
        setPhase("end");
      }
    }
  };

  const inRange = (ds) => {
    const e = end || hover;
    if (!start || !e || e <= start) return false;
    return ds > start && ds < e;
  };

  const prev = () => { if (mo === 0) { setMo(11); setYr(y=>y-1); } else setMo(m=>m-1); };
  const next = () => { if (mo === 11){ setMo(0);  setYr(y=>y+1); } else setMo(m=>m+1); };

  function Month({ y, m }) {
    const dim   = daysInMonth(y,m);
    const fd    = firstDayOf(y,m);
    const cells = [...Array(fd).fill(null), ...Array.from({length:dim},(_,i)=>i+1)];
    return (
      <div className="flex-1">
        <p className="text-center font-bold text-sm text-gray-900 mb-4">{MONTHS[m]} {y}</p>
        <div className="grid grid-cols-7 mb-1">
          {WDAYS.map(d => <div key={d} className="text-center text-xs text-gray-400 font-medium py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((d,i) => {
            if (!d) return <div key={`e${i}`} />;
            const ds       = datStr(y,m,d);
            const isPast   = ds < today;
            const disabled = isPast;
            const isS      = ds === start;
            const isE      = ds === end;
            const rang     = inRange(ds);
            return (
              <div key={d} className="relative flex items-center justify-center h-9">
                {rang        && <div className="absolute inset-y-1 left-0 right-0 bg-blue-50" />}
                {isS && end  && <div className="absolute inset-y-1 left-1/2 right-0 bg-blue-50" />}
                {isE && start && <div className="absolute inset-y-1 left-0 right-1/2 bg-blue-50" />}
                <button
                  onClick={() => !disabled && pick(ds)}
                  onMouseEnter={() => !disabled && phase === "end" && setHover(ds)}
                  onMouseLeave={() => setHover(null)}
                  disabled={disabled}
                  className={`relative z-10 w-8 h-8 text-sm rounded-full transition-all
                    ${disabled ? "text-gray-300 cursor-not-allowed" : "cursor-pointer"}
                    ${isS || isE ? "bg-[#090e4c] text-white" : ""}
                    ${!isS && !isE && !disabled ? "text-gray-700 hover:bg-gray-100" : ""}
                  `}
                >{d}</button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6" style={{ width: singleDate ? 320 : 660, maxWidth: "95vw" }}>
      {/* Selected range header */}
      <div className="flex items-center gap-4 mb-4 pb-3 border-b border-gray-100">
        <div>
          <p className="text-xs text-gray-400 font-medium mb-0.5">
            {phase === "end" && start ? "Now pick return date" : "Departure"}
          </p>
          <span className={`text-base font-bold ${start ? "text-gray-900" : "text-gray-300"}`}>
            {start ? fmtDate(start) : "—"}
          </span>
        </div>
        {!singleDate && <>
          <span className="text-gray-300 text-lg">→</span>
          <div>
            <p className="text-xs text-gray-400 font-medium mb-0.5">Return</p>
            <span className={`text-base font-bold ${end ? "text-gray-900" : "text-gray-400"}`}>
              {end ? fmtDate(end) : "—"}
            </span>
          </div>
        </>}
      </div>

      {/* Calendar months */}
      <div className="flex items-start gap-2">
        <button onClick={prev} aria-label="Previous month" className="mt-6 p-2 hover:bg-gray-100 rounded-full text-gray-500 text-lg">‹</button>
        <div className="flex gap-8 flex-1">
          <Month y={yr} m={mo} />
          {!singleDate && <Month y={r_yr} m={r_mo} />}
        </div>
        <button onClick={next} aria-label="Next month" className="mt-6 p-2 hover:bg-gray-100 rounded-full text-gray-500 text-lg">›</button>
      </div>

      {/* Flexibility toggle — shown for roundtrip and one-way; hidden for multi-city legs (autoClose) */}
      {!autoClose && (
        <div className="mt-5 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-500 font-medium mb-2">Date flexibility</p>
          <div className="flex gap-2 flex-wrap">
            {FLEX_OPTIONS.map(o => (
              <button
                key={o.value}
                onClick={() => handleFlexChange(o.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors
                  ${flex === o.value
                    ? "bg-[#090e4c] text-white border-[#090e4c]"
                    : "bg-white text-gray-600 border-gray-300 hover:border-[#090e4c] hover:text-[#090e4c]"
                  }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end mt-4">
        <button
          onClick={() => { onChange(start, end, flex); onClose(); }}
          className="bg-[#090e4c] text-white px-8 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 cursor-pointer"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ── Travelers popup ────────────────────────────────────────────────────────────
function Counter({ val, onDec, onInc, min=0 }) {
  return (
    <div className="flex items-center gap-3">
      <button onClick={onDec} disabled={val<=min}
        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-lg leading-none
          ${val<=min?"border-gray-200 text-gray-300 cursor-default":"border-gray-400 text-gray-600 hover:border-[#090e4c] hover:text-[#090e4c] cursor-pointer"}`}
      >−</button>
      <span className="w-4 text-center text-sm font-semibold text-gray-900">{val}</span>
      <button onClick={onInc}
        className="w-8 h-8 rounded-full border-2 border-gray-400 text-gray-600 hover:border-[#090e4c] hover:text-[#090e4c] flex items-center justify-center text-lg leading-none cursor-pointer"
      >+</button>
    </div>
  );
}

function TravelersPopup({ pax, cabin, onPax, onCabin, onClose }) {
  return (
    <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 w-80">
      <h2 className="font-bold text-gray-900 mb-5 text-base">Travelers and Cabin class</h2>
      <div className="space-y-5">
        {[
          { key:"adults",      label:"Adults",          sub:null },
          { key:"children",    label:"Children",        sub:"Ages 2 to 17" },
          { key:"infantsLap",  label:"Infants on lap",  sub:"Younger than 2" },
          { key:"infantsSeat", label:"Infants in seat", sub:"Younger than 2" },
        ].map(({ key, label, sub }) => (
          <div key={key} className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">{label}</p>
              {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
            </div>
            <Counter
              val={pax[key]}
              min={key==="adults"?1:0}
              onDec={() => onPax(key, Math.max(key==="adults"?1:0, pax[key]-1))}
              onInc={() => onPax(key, pax[key]+1)}
            />
          </div>
        ))}
        <div className="pt-3 border-t border-gray-100">
          <label className="text-xs text-gray-500 block mb-2">Cabin class</label>
          <div className="relative">
            <select value={cabin} onChange={e=>onCabin(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-[#090e4c] bg-white appearance-none cursor-pointer pr-8">
              <option value="economy">Economy</option>
              <option value="premium_economy">Premium Economy</option>
              <option value="business">Business</option>
              <option value="first">First Class</option>
            </select>
            <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
          </div>
        </div>
      </div>
      <div className="flex justify-end mt-5">
        <button onClick={onClose} className="bg-[#090e4c] text-white px-8 py-2.5 rounded-full text-sm font-semibold hover:opacity-90">Done</button>
      </div>
    </div>
  );
}

// ── Shared icons ───────────────────────────────────────────────────────────────
const PinIcon = () => (
  <svg aria-hidden="true" className="w-4 h-4 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="10" r="3"/><path d="M12 2a8 8 0 0 0-8 8c0 5.25 8 13 8 13s8-7.75 8-13a8 8 0 0 0-8-8z"/>
  </svg>
);
const CalIcon = () => (
  <svg aria-hidden="true" className="w-4 h-4 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const PersonIcon = () => (
  <svg aria-hidden="true" className="w-4 h-4 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

const CABIN_LABELS = { economy:"Economy", premium_economy:"Premium Economy", business:"Business", first:"First Class" };

// Borderless input class for inside Expedia boxes
const INNER = "flex-1 min-w-0 border-none outline-none focus-visible:outline-none bg-transparent text-sm font-medium text-gray-900 placeholder:text-gray-400 p-0 leading-tight";

// ── Main export ────────────────────────────────────────────────────────────────
export const HeroSection = () => {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteData, setQuoteData] = useState(null);
  const [activeTab, setActiveTab] = useState("flights");

  // Flights
  const [tripType, setTripType] = useState("roundtrip");
  const [fromVal,  setFromVal]  = useState(""); const [fromCode, setFromCode] = useState("");
  const [toVal,    setToVal]    = useState(""); const [toCode,   setToCode]   = useState("");
  const [depart,   setDepart]   = useState("");
  const [ret,      setRet]      = useState("");
  const [dateFlex, setDateFlex] = useState(0);
  const [pax,      setPax]      = useState({ adults:1, children:0, infantsLap:0, infantsSeat:0 });
  const [cabin,    setCabin]    = useState("economy");
  const [showCal,  setShowCal]  = useState(false);
  const [showPax,  setShowPax]  = useState(false);

  // Multi-city
  const [mcFlights, setMcFlights] = useState([
    { from:"", fromCode:"", to:"", toCode:"", date:"" },
    { from:"", fromCode:"", to:"", toCode:"", date:"" },
  ]);
  const [mcShowCal, setMcShowCal] = useState(null); // index of open calendar

  const mcUpdate = (i, field, val) =>
    setMcFlights(prev => prev.map((f, idx) => idx===i ? {...f, [field]:val} : f));

  const mcSwap = (i) =>
    setMcFlights(prev => prev.map((f, idx) => idx===i ? {...f, from:f.to, fromCode:f.toCode, to:f.from, toCode:f.fromCode} : f));

  const mcAddFlight = () =>
    setMcFlights(prev => [...prev, { from:"", fromCode:"", to:"", toCode:"", date:"" }]);

  const mcRemoveFlight = (i) =>
    setMcFlights(prev => prev.filter((_,idx) => idx!==i));

  // Tours
  const [tourDest, setTourDest] = useState("");
  const [tourDate, setTourDate] = useState("");
  const [tourDur,  setTourDur]  = useState("");
  const [tourPax,  setTourPax]  = useState("2");

  // Cruises
  const [cRegion,  setCRegion]  = useState("");
  const [cDate,    setCDate]    = useState("");
  const [cNights,  setCNights]  = useState("");
  const [cGuests,  setCGuests]  = useState("2");

  const calRef = useRef(null);
  const paxRef = useRef(null);

  useEffect(() => {
    const fn = (e) => {
      if (calRef.current && !calRef.current.contains(e.target)) setShowCal(false);
      if (paxRef.current && !paxRef.current.contains(e.target)) setShowPax(false);
      // Close multi-city calendars if clicked outside all calendar containers
      setMcShowCal(prev => {
        if (prev === null) return null;
        const el = document.querySelector(`[data-mc-cal="${prev}"]`);
        if (el && !el.contains(e.target)) return null;
        return prev;
      });
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  const today    = todayStr();
  const totalPax = pax.adults + pax.children + pax.infantsLap + pax.infantsSeat;
  const paxLabel = `${totalPax} traveler${totalPax!==1?"s":""}, ${CABIN_LABELS[cabin]}`;
  const flexSuffix = dateFlex > 0 ? ` (±${dateFlex}d)` : "";
  const datesLabel = depart
    ? (ret && tripType==="roundtrip"
        ? `${fmtDate(depart)} – ${fmtDate(ret)}${flexSuffix}`
        : `${fmtDate(depart)}${flexSuffix}`)
    : null;

  const swap = () => {
    const tf=fromVal, tfc=fromCode;
    setFromVal(toVal); setFromCode(toCode);
    setToVal(tf); setToCode(tfc);
  };

  const searchFlight = () => {
    if (tripType === "multicity") {
      const legs = mcFlights.map((f,i) => `Flight ${i+1}: ${f.from||"—"} → ${f.to||"—"}${f.date?" on "+fmtDate(f.date):""}`).join("; ");
      setQuoteData({ type:"flight", from:mcFlights[0]?.from||"", to:mcFlights[mcFlights.length-1]?.to||"", passengers:String(totalPax), cabinClass:cabin, tripType, multiCityLegs:legs });
    } else {
      setQuoteData({ type:"flight", from:fromVal, fromCode, to:toVal, toCode, departure:depart, returnDate:tripType==="roundtrip"?ret:"", passengers:String(totalPax), cabinClass:cabin, tripType, dateFlexibility: dateFlex > 0 ? `±${dateFlex} day${dateFlex>1?"s":""}` : "Exact dates" });
    }
    setQuoteOpen(true);
  };
  const searchTour   = () => { setQuoteData({ type:"tour",   destination:tourDest, travelDate:tourDate, duration:tourDur, travelers:tourPax }); setQuoteOpen(true); };
  const searchCruise = () => { setQuoteData({ type:"cruise", region:cRegion, departureDate:cDate, nights:cNights, passengers:cGuests }); setQuoteOpen(true); };

  return (
    <section id="main-content" className="relative pt-12 lg:pt-16 min-h-[520px] lg:min-h-[560px]" data-testid="hero-section">
      <HeroBackground />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Headline */}
        <div className="text-center max-w-2xl mx-auto mb-6">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 [text-wrap:balance]">One place to go for all your travel</h1>
          <p className="text-sm lg:text-base text-white/80">Flights, tours, cruises — all in one search</p>
        </div>

        {/* Search card — NO overflow-hidden so dropdowns aren't clipped */}
        <div
          className="bg-white rounded-2xl max-w-6xl mx-auto"
          style={{ boxShadow:"0 20px 60px rgba(9,14,76,.25)" }}
        >
          <Tabs defaultValue="flights" className="w-full" onValueChange={setActiveTab}>
            {/* Category tabs */}
            <TabsList className="w-full justify-start bg-transparent border-b border-gray-200 rounded-t-2xl h-auto p-0">
              <TabsTrigger value="flights"
                className="rounded-none rounded-tl-2xl data-[state=active]:border-b-2 data-[state=active]:border-[#21b3ed] data-[state=active]:text-[#090e4c] data-[state=active]:bg-white px-5 py-3 text-sm font-medium text-gray-500 hover:text-[#090e4c] transition-colors bg-transparent">
                <span className="flex items-center gap-2">
                  <FlightTabIcon active={activeTab==="flights"} />
                  Flights
                </span>
              </TabsTrigger>
              <TabsTrigger value="tours"
                className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#21b3ed] data-[state=active]:text-[#090e4c] data-[state=active]:bg-white px-5 py-3 text-sm font-medium text-gray-500 hover:text-[#090e4c] transition-colors bg-transparent">
                <span className="flex items-center gap-2">
                  <ToursTabIcon active={activeTab==="tours"} />
                  Tours
                </span>
              </TabsTrigger>
              <TabsTrigger value="cruises"
                className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#21b3ed] data-[state=active]:text-[#090e4c] data-[state=active]:bg-white px-5 py-3 text-sm font-medium text-gray-500 hover:text-[#090e4c] transition-colors bg-transparent">
                <span className="flex items-center gap-2">
                  <CruisesTabIcon active={activeTab==="cruises"} />
                  Cruises
                </span>
              </TabsTrigger>
            </TabsList>

            {/* ─── FLIGHTS ─────────────────────────────────────────────────── */}
            <TabsContent value="flights" className="p-5 sm:p-6 pb-6">
              {/* Trip type selector */}
              <div className="flex gap-6 mb-5 pb-3 border-b border-gray-100">
                {[["roundtrip","Roundtrip"],["oneway","One-way"],["multicity","Multi-city"]].map(([v,l]) => (
                  <button key={v} onClick={() => setTripType(v)}
                    className={`text-sm font-medium pb-1 border-b-2 transition-colors
                      ${tripType===v ? "text-[#090e4c] border-[#21b3ed]" : "text-gray-500 border-transparent hover:text-[#090e4c]"}`}>
                    {l}
                  </button>
                ))}
              </div>

              {tripType === "multicity" ? (
                /* ── MULTI-CITY LAYOUT ── */
                <div className="space-y-1">
                  {/* Travelers at top */}
                  <div className="mb-4" ref={paxRef}>
                    <div style={{ position:"relative", display:"inline-block" }}>
                      <button
                        type="button"
                        onClick={() => setShowPax(p=>!p)}
                        className="flex items-center gap-3 border border-gray-300 rounded-lg px-4 py-3 bg-white hover:border-gray-400 transition-colors"
                      >
                        <PersonIcon />
                        <div className="text-left">
                          <div className="text-xs text-gray-500 leading-none mb-1">Travelers, Cabin class</div>
                          <div className="text-sm font-medium text-gray-900 leading-none">{paxLabel}</div>
                        </div>
                      </button>
                      {showPax && (
                        <div className="absolute top-full left-0 mt-2 z-[9999]">
                          <TravelersPopup pax={pax} cabin={cabin} onPax={(k,v)=>setPax(p=>({...p,[k]:v}))} onCabin={setCabin} onClose={()=>setShowPax(false)} />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Flight legs */}
                  {mcFlights.map((leg, i) => (
                    <div key={i} className="mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold text-gray-800">Flight {i + 1}</p>
                        {i >= 2 && (
                          <button onClick={() => mcRemoveFlight(i)} className="text-xs text-gray-400 hover:text-red-500 transition-colors">
                            ✕ Remove
                          </button>
                        )}
                      </div>
                      <div className="flex flex-col lg:flex-row gap-2 items-stretch lg:items-center">
                        {/* From / Swap / To pill — takes remaining space */}
                        <div className="flex-1 min-w-0 flex items-center" style={{ minWidth:0 }}>
                          <div className="flex-1 min-w-0 flex items-center gap-2 border border-gray-300 rounded-full px-3 py-2.5 bg-white hover:border-gray-400 focus-within:border-gray-500 transition-colors pr-8">
                            <PinIcon />
                            <AirportAutocomplete
                              value={leg.from}
                              onChange={v => mcUpdate(i,"from",v)}
                              onSelect={a => { mcUpdate(i,"from",`${a.city} (${a.code})`); mcUpdate(i,"fromCode",a.code); }}
                              placeholder="Leaving from"
                              wrapperClassName="flex-1 min-w-0"
                              inputClassName={INNER}
                            />
                          </div>
                          <button
                            onClick={() => mcSwap(i)}
                            aria-label={`Swap departure and arrival for flight ${i + 1}`}
                            className="-mx-5 z-10 flex-shrink-0 w-9 h-9 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center text-[#21b3ed] hover:border-gray-400 hover:text-[#090e4c] transition-colors shadow-sm cursor-pointer"
                          >
                            <svg aria-hidden="true" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M8 7h12m0 0l-4-4m4 4l-4 4M16 17H4m0 0l4 4m-4-4l4-4"/>
                            </svg>
                          </button>
                          <div className="flex-1 min-w-0 flex items-center gap-2 border border-gray-300 rounded-full px-3 py-2.5 bg-white hover:border-gray-400 focus-within:border-gray-500 transition-colors pl-8">
                            <PinIcon />
                            <AirportAutocomplete
                              value={leg.to}
                              onChange={v => mcUpdate(i,"to",v)}
                              onSelect={a => { mcUpdate(i,"to",`${a.city} (${a.code})`); mcUpdate(i,"toCode",a.code); }}
                              placeholder="Going to"
                              wrapperClassName="flex-1 min-w-0"
                              inputClassName={INNER}
                            />
                          </div>
                        </div>
                        {/* Date for this leg — fixed width, slightly wider */}
                        <div className="relative w-full lg:w-48" data-mc-cal={i}>
                          <button
                            type="button"
                            onClick={() => setMcShowCal(mcShowCal===i ? null : i)}
                            className="flex items-center gap-3 border border-gray-300 rounded-lg px-4 py-2.5 bg-white hover:border-gray-400 transition-colors w-full"
                          >
                            <CalIcon />
                            <div className="text-left">
                              <div className="text-xs text-gray-500 leading-none mb-1">Date</div>
                              <div className={`text-sm font-medium leading-none ${leg.date?"text-gray-900":"text-gray-400"}`}>
                                {leg.date ? fmtDate(leg.date) : "Add date"}
                              </div>
                            </div>
                          </button>
                          {mcShowCal === i && (
                            <div className="absolute top-full left-0 mt-2 z-[9999]">
                              <DateRangePicker
                                start={leg.date}
                                end={null}
                                singleDate={true}
                                autoClose={true}
                                onChange={(s) => { mcUpdate(i,"date",s||""); setMcShowCal(null); }}
                                onClose={() => setMcShowCal(null)}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Footer row */}
                  <div className="flex items-center justify-between pt-2">
                    {mcFlights.length < 5 ? (
                      <button onClick={mcAddFlight} className="flex items-center gap-1 text-sm text-[#21b3ed] hover:text-[#090e4c] font-medium transition-colors">
                        <span className="text-lg leading-none">+</span> Add another flight
                      </button>
                    ) : <div />}
                    <button onClick={searchFlight} className="bg-[#21b3ed] hover:bg-[#1aa0d8] text-white font-bold px-8 py-3 rounded-full transition-colors text-sm">
                      Search
                    </button>
                  </div>
                </div>

              ) : (
                /* ── ROUNDTRIP / ONE-WAY LAYOUT ── */
                <div className="flex flex-col lg:flex-row gap-2 items-stretch lg:items-center">
                  {/* From / Swap / To pills */}
                  <div className="flex-1 min-w-0 flex items-center">
                    <div className="flex-1 min-w-0 flex items-center gap-2 border border-gray-300 rounded-full px-4 py-3 bg-white hover:border-gray-400 focus-within:border-gray-500 transition-colors pr-8">
                      <PinIcon />
                      <AirportAutocomplete
                        value={fromVal}
                        onChange={setFromVal}
                        onSelect={a => { setFromVal(`${a.city} (${a.code})`); setFromCode(a.code); }}
                        placeholder="Leaving from"
                        wrapperClassName="flex-1 min-w-0"
                        inputClassName={INNER}
                      />
                    </div>
                    <button
                      onClick={swap}
                      aria-label="Swap departure and arrival airports"
                      className="-mx-5 z-10 flex-shrink-0 w-10 h-10 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center text-[#21b3ed] hover:border-gray-400 hover:text-[#090e4c] transition-colors shadow-sm cursor-pointer"
                    >
                      <svg aria-hidden="true" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M8 7h12m0 0l-4-4m4 4l-4 4M16 17H4m0 0l4 4m-4-4l4-4"/>
                      </svg>
                    </button>
                    <div className="flex-1 min-w-0 flex items-center gap-2 border border-gray-300 rounded-full px-4 py-3 bg-white hover:border-gray-400 focus-within:border-gray-500 transition-colors pl-8">
                      <PinIcon />
                      <AirportAutocomplete
                        value={toVal}
                        onChange={setToVal}
                        onSelect={a => { setToVal(`${a.city} (${a.code})`); setToCode(a.code); }}
                        placeholder="Going to"
                        wrapperClassName="flex-1 min-w-0"
                        inputClassName={INNER}
                      />
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="relative flex-shrink-0" ref={calRef}>
                    <button
                      type="button"
                      onClick={() => { setShowCal(p=>!p); setShowPax(false); }}
                      className="flex items-center gap-3 border border-gray-300 rounded-lg px-4 py-3 bg-white hover:border-gray-400 transition-colors whitespace-nowrap w-full lg:w-auto"
                    >
                      <CalIcon />
                      <div className="text-left">
                        <div className="text-xs text-gray-500 leading-none mb-1">Dates</div>
                        <div className={`text-sm font-medium leading-none ${datesLabel?"text-gray-900":"text-gray-400"}`}>
                          {datesLabel || "Add dates"}
                        </div>
                      </div>
                    </button>
                    {showCal && (
                      <div className="absolute top-full left-0 mt-2 z-[9999]">
                        <DateRangePicker
                          start={depart}
                          end={tripType==="roundtrip" ? ret : null}
                          singleDate={tripType==="oneway"}
                          flexibility={dateFlex}
                          onFlexChange={setDateFlex}
                          onChange={(s, e, f) => { setDepart(s||""); setRet(e||""); if (f !== undefined) setDateFlex(f); }}
                          onClose={() => setShowCal(false)}
                        />
                      </div>
                    )}
                  </div>

                  {/* Travelers */}
                  <div className="relative flex-shrink-0" ref={paxRef}>
                    <button
                      type="button"
                      onClick={() => { setShowPax(p=>!p); setShowCal(false); }}
                      className="flex items-center gap-3 border border-gray-300 rounded-lg px-4 py-3 bg-white hover:border-gray-400 transition-colors whitespace-nowrap w-full lg:w-auto"
                    >
                      <PersonIcon />
                      <div className="text-left">
                        <div className="text-xs text-gray-500 leading-none mb-1">Travelers, Cabin class</div>
                        <div className="text-sm font-medium text-gray-900 leading-none">{paxLabel}</div>
                      </div>
                    </button>
                    {showPax && (
                      <div className="absolute top-full right-0 mt-2 z-[9999]">
                        <TravelersPopup pax={pax} cabin={cabin} onPax={(k,v)=>setPax(p=>({...p,[k]:v}))} onCabin={setCabin} onClose={()=>setShowPax(false)} />
                      </div>
                    )}
                  </div>

                  {/* Search */}
                  <button onClick={searchFlight} className="flex-shrink-0 bg-[#21b3ed] hover:bg-[#1aa0d8] text-white font-bold px-8 py-3 rounded-full transition-colors text-sm">
                    Search
                  </button>
                </div>
              )}
            </TabsContent>

            {/* ─── TOURS ──────────────────────────────────────────────────── */}
            <TabsContent value="tours" className="p-5 sm:p-6 pb-6">
              <div className="flex flex-col lg:flex-row gap-2 items-stretch lg:items-center">
                {/* Destination */}
                <div className="flex-1 min-w-0 flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-3 bg-white hover:border-gray-400 focus-within:border-gray-600 transition-colors">
                  <PinIcon />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-500 mb-0.5">Destination</div>
                    <DestinationAutocomplete
                      value={tourDest}
                      onChange={setTourDest}
                      onSelect={d=>setTourDest(`${d.city}, ${d.country}`)}
                      placeholder="Where to?"
                      wrapperClassName="w-full"
                      inputClassName={INNER}
                    />
                  </div>
                </div>
                {/* Travel date */}
                <div className="flex-shrink-0 flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-3 bg-white hover:border-gray-400 transition-colors">
                  <CalIcon />
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Travel Date</div>
                    <input type="date" value={tourDate} min={today} onChange={e=>setTourDate(e.target.value)}
                      className={INNER} style={{colorScheme:"light"}} />
                  </div>
                </div>
                {/* Duration */}
                <div className="flex-shrink-0 border border-gray-300 rounded-lg px-4 py-3 bg-white hover:border-gray-400 transition-colors">
                  <div className="text-xs text-gray-500 mb-1">Duration</div>
                  <select value={tourDur} onChange={e=>setTourDur(e.target.value)}
                    className="text-sm font-medium text-gray-900 border-none outline-none bg-transparent cursor-pointer">
                    <option value="">Select duration</option>
                    {["3-5 days","5-7 days","7-10 days","10-14 days","14+ days"].map(v=><option key={v}>{v}</option>)}
                  </select>
                </div>
                {/* Travelers */}
                <div className="flex-shrink-0 flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-3 bg-white hover:border-gray-400 transition-colors">
                  <PersonIcon />
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Travelers</div>
                    <input type="number" min="1" max="20" value={tourPax} onChange={e=>setTourPax(e.target.value)}
                      className={`${INNER} w-10`} />
                  </div>
                </div>
                <button onClick={searchTour}
                  className="flex-shrink-0 bg-[#21b3ed] hover:bg-[#1aa0d8] text-white font-bold px-8 py-3 rounded-full transition-colors text-sm">Search</button>
              </div>
            </TabsContent>

            {/* ─── CRUISES ────────────────────────────────────────────────── */}
            <TabsContent value="cruises" className="p-5 sm:p-6 pb-6">
              <div className="flex flex-col lg:flex-row gap-2 items-stretch lg:items-center">
                {/* Region */}
                <div className="flex-1 min-w-0 flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-3 bg-white hover:border-gray-400 focus-within:border-gray-600 transition-colors">
                  <PinIcon />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-gray-500 mb-0.5">Region / Port</div>
                    <CruisePortAutocomplete
                      value={cRegion}
                      onChange={setCRegion}
                      onSelect={item=>setCRegion(item.country?`${item.name}, ${item.country}`:item.name)}
                      placeholder="Caribbean, Mediterranean..."
                      wrapperClassName="w-full"
                      inputClassName={INNER}
                    />
                  </div>
                </div>
                {/* Date */}
                <div className="flex-shrink-0 flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-3 bg-white hover:border-gray-400 transition-colors">
                  <CalIcon />
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Departure Date</div>
                    <input type="date" value={cDate} min={today} onChange={e=>setCDate(e.target.value)}
                      className={INNER} style={{colorScheme:"light"}} />
                  </div>
                </div>
                {/* Duration */}
                <div className="flex-shrink-0 border border-gray-300 rounded-lg px-4 py-3 bg-white hover:border-gray-400 transition-colors">
                  <div className="text-xs text-gray-500 mb-1">Duration</div>
                  <select value={cNights} onChange={e=>setCNights(e.target.value)}
                    className="text-sm font-medium text-gray-900 border-none outline-none bg-transparent cursor-pointer">
                    <option value="">Select duration</option>
                    {["3-5 nights","5-7 nights","7-10 nights","10-14 nights","14+ nights"].map(v=><option key={v}>{v}</option>)}
                  </select>
                </div>
                {/* Guests */}
                <div className="flex-shrink-0 flex items-center gap-2 border border-gray-300 rounded-lg px-4 py-3 bg-white hover:border-gray-400 transition-colors">
                  <PersonIcon />
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Guests</div>
                    <input type="number" min="1" max="20" value={cGuests} onChange={e=>setCGuests(e.target.value)}
                      className={`${INNER} w-10`} />
                  </div>
                </div>
                <button onClick={searchCruise}
                  className="flex-shrink-0 bg-[#21b3ed] hover:bg-[#1aa0d8] text-white font-bold px-8 py-3 rounded-full transition-colors text-sm">Search</button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <QuoteRequestDialog open={quoteOpen} onOpenChange={setQuoteOpen} quoteData={quoteData} />
    </section>
  );
};
